import React, { useState, useRef, useEffect } from 'react';

interface DataPoint {
  x: number | string;
  y: number | null;
}

interface LineData {
  name: string;
  data: DataPoint[];
  color: string;
  axis?: 'primary' | 'secondary';
}

interface MiniLineChartProps {
  lines: LineData[];
  width?: number;
  height?: number;
  className?: string;
}

// Color palette from the screenshot
const DEFAULT_COLORS = [
  '#ECF5C0', // Light green
  '#D3EEF2', // Light blue
  '#A2BBBF', // Blue-grey (existing brand color)
  '#879643', // Olive green
  '#333435', // Dark grey
  '#1C1C1C', // Very dark grey
];

const MiniLineChart: React.FC<MiniLineChartProps> = ({ 
  lines, 
  width, 
  height = 200, 
  className = '' 
}) => {
  const [hoveredPoint, setHoveredPoint] = useState<{
    pointIndex: number;
    x: number;
    xValue?: number | string;
    values: Array<{
      lineIndex: number;
      y: number | null;
      value: DataPoint;
    }>;
  } | null>(null);
  
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(width || 400);
  
  // Responsive width handling
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current && !width) {
        const newWidth = containerRef.current.offsetWidth;
        setContainerWidth(newWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [width]);

  // Chart dimensions
  const effectiveWidth = width || containerWidth;
  // Use minimal padding, with extra space for secondary axis if needed
  const hasSecondaryAxis = lines.some(line => line.axis === 'secondary');
  const basePadding = 5;
  const margin = {
    top: 10,
    right: hasSecondaryAxis ? basePadding + 40 : basePadding, // Room for secondary-axis tick labels (abbreviated values can be ~30px wide)
    bottom: 25,
    left: 40
  };
  const chartWidth = effectiveWidth - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  
  // Separate primary and secondary axis data
  const primaryLines = lines.filter(line => line.axis !== 'secondary');
  const secondaryLines = lines.filter(line => line.axis === 'secondary');
  
  // Calculate scales for primary axis
  const primaryYValues = primaryLines.flatMap(line => line.data.map(d => d.y)).filter((y): y is number => y !== null);
  const allXValues = lines.flatMap(line => line.data.map(d => d.x));
  
  const yMin = primaryYValues.length > 0 ? Math.min(...primaryYValues) : 0;
  const yMax = primaryYValues.length > 0 ? Math.max(...primaryYValues) : 100;
  const yRange = yMax - yMin;
  
  // Add padding to primary y-axis
  const yPadding = yRange * 0.1;
  const yScaleMin = yMin - yPadding;
  const yScaleMax = yMax + yPadding;
  const yScaleRange = yScaleMax - yScaleMin;
  
  // Calculate scales for secondary axis if needed
  let secondaryYMin = 0, secondaryYMax = 100, secondaryYScaleMin = 0, secondaryYScaleMax = 100, secondaryYScaleRange = 100;
  if (hasSecondaryAxis) {
    const secondaryYValues = secondaryLines.flatMap(line => line.data.map(d => d.y)).filter((y): y is number => y !== null);
    secondaryYMin = secondaryYValues.length > 0 ? Math.min(...secondaryYValues) : 0;
    secondaryYMax = secondaryYValues.length > 0 ? Math.max(...secondaryYValues) : 100;
    const secondaryYRange = secondaryYMax - secondaryYMin;
    const secondaryYPadding = secondaryYRange * 0.1;
    secondaryYScaleMin = secondaryYMin - secondaryYPadding;
    secondaryYScaleMax = secondaryYMax + secondaryYPadding;
    secondaryYScaleRange = secondaryYScaleMax - secondaryYScaleMin;
  }
  
  // X-axis handling (assume numeric or convert to indices)
  const xMin = 0;
  const xMax = Math.max(...lines.map(line => line.data.length - 1));
  
  // Scale functions
  const scaleX = (x: number | string) => {
    if (typeof x === 'string') {
      // If string, use index position
      const line = lines.find(l => l.data.some(d => d.x === x));
      if (line) {
        const index = line.data.findIndex(d => d.x === x);
        return (index / (line.data.length - 1)) * chartWidth;
      }
      return 0;
    }
    return ((x - xMin) / (xMax - xMin)) * chartWidth;
  };
  
  const scaleY = (y: number) => {
    return chartHeight - ((y - yScaleMin) / yScaleRange) * chartHeight;
  };
  
  const scaleSecondaryY = (y: number) => {
    return chartHeight - ((y - secondaryYScaleMin) / secondaryYScaleRange) * chartHeight;
  };
  
  const formatTickLabel = (value: number): string => {
    const abs = Math.abs(value);
    if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 10_000) return `${(value / 1_000).toFixed(1)}K`;
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  };

  // Generate Y-axis ticks
  const yTicks = [];
  const tickCount = 4;
  for (let i = 0; i <= tickCount; i++) {
    const value = yScaleMin + (yScaleRange * i) / tickCount;
    yTicks.push(value);
  }
  
  // Generate path for each line
  // KNOWN LIMITATION: a non-null point with null neighbours on both sides becomes a
  // lone `M` command with no following `L`, which SVG draws as nothing. For sparse or
  // alternating data (value, null, value, null) the whole series can render invisible.
  // Fixing this means also emitting a <circle> for single-point segments — deferred as
  // it changes this function's return contract. See PR #544 review.
  const generatePath = (data: DataPoint[], useSecondaryAxis = false) => {
    if (data.length === 0) return '';

    const yScale = useSecondaryAxis ? scaleSecondaryY : scaleY;

    let path = '';
    let newSegment = true;
    data.forEach((point, index) => {
      if (point.y === null) {
        newSegment = true;
        return;
      }
      const x = scaleX(index);
      const y = yScale(point.y);

      if (newSegment) {
        path += `M ${x} ${y}`;
        newSegment = false;
      } else {
        path += ` L ${x} ${y}`;
      }
    });

    return path;
  };
  
  // Handle mouse events
  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    
    const rect = svgRef.current.getBoundingClientRect();
    const mouseX = event.clientX - rect.left - margin.left;
    
    const maxPoints = Math.max(...lines.map(line => line.data.length));
    if (maxPoints <= 0) {
      setHoveredPoint(null);
      return;
    }

    const clampedX = Math.min(Math.max(mouseX, 0), chartWidth);
    const rawIndex = maxPoints === 1 ? 0 : (clampedX / chartWidth) * (maxPoints - 1);
    const pointIndex = Math.round(rawIndex);
    const xPos = margin.left + (maxPoints === 1 ? 0 : (pointIndex / (maxPoints - 1)) * chartWidth);

    const values = lines
      .map((line, lineIndex) => {
        const point = line.data[pointIndex];
        if (!point) return null;
        const useSecondaryAxis = line.axis === 'secondary';
        const yScale = useSecondaryAxis ? scaleSecondaryY : scaleY;
        return {
          lineIndex,
          y: point.y !== null ? yScale(point.y) : null,
          value: point
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    if (values.length === 0) {
      setHoveredPoint(null);
      return;
    }

    const xValue = lines[0]?.data[pointIndex]?.x;
    setHoveredPoint({
      pointIndex,
      x: xPos,
      xValue,
      values
    });
  };
  
  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };
  
  return (
    <div 
      ref={containerRef}
      className={`relative ${className}`} 
      style={{ width: width || '100%', height }}
    >
      <svg
        ref={svgRef}
        width={effectiveWidth}
        height={height}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="overflow-visible"
      >
        {/* Chart background - transparent */}
        <rect
          x={0}
          y={0}
          width={effectiveWidth}
          height={height}
          fill="transparent"
          stroke="none"
        />
        
        {/* Subtle vertical grid lines */}
        {lines.length > 0 && lines[0].data.map((_, index) => (
          index % Math.ceil(lines[0].data.length / 6) === 0 && ( // Show ~6 vertical lines
            <line
              key={`grid-v-${index}`}
              x1={margin.left + scaleX(index)}
              y1={margin.top}
              x2={margin.left + scaleX(index)}
              y2={margin.top + chartHeight}
              stroke="#F3F4F6"
              strokeWidth={1}
            />
          )
        ))}
        
        {/* Subtle horizontal grid lines */}
        {yTicks.map((tick, index) => (
          <line
            key={`grid-h-${index}`}
            x1={margin.left}
            y1={margin.top + scaleY(tick)}
            x2={margin.left + chartWidth}
            y2={margin.top + scaleY(tick)}
            stroke="#F3F4F6"
            strokeWidth={1}
          />
        ))}
        
        {/* Y-axis labels */}
        {yTicks.map((tick, index) => (
          <text
            key={`y-label-${index}`}
            x={margin.left - 6}
            y={margin.top + scaleY(tick)}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize="10"
            fill="#9CA3AF"
          >
            {formatTickLabel(tick)}
          </text>
        ))}

        {/* Secondary y-axis ticks and labels (right side) */}
        {hasSecondaryAxis && (() => {
          const secondaryTicks = [];
          for (let i = 0; i <= 4; i++) {
            const value = secondaryYScaleMin + (secondaryYScaleRange * i) / 4;
            secondaryTicks.push(value);
          }
          return secondaryTicks.map((tick, index) => (
            <g key={`secondary-tick-${index}`}>
              <line
                x1={margin.left + chartWidth}
                y1={margin.top + scaleSecondaryY(tick)}
                x2={margin.left + chartWidth + 5}
                y2={margin.top + scaleSecondaryY(tick)}
                stroke="#9CA3AF"
                strokeWidth={1}
              />
              <text
                x={margin.left + chartWidth + 8}
                y={margin.top + scaleSecondaryY(tick)}
                textAnchor="start"
                dominantBaseline="middle"
                fontSize="10"
                fill="#9CA3AF"
              >
                {formatTickLabel(tick)}
              </text>
            </g>
          ));
        })()}
        
        {/* X-axis labels (multiple evenly spaced labels) */}
        {lines.length > 0 && lines[0].data.length > 1 && (() => {
          const dataLength = lines[0].data.length;
          const maxLabels = Math.min(6, dataLength); // Show up to 6 labels
          const labelIndices = [];
          
          // Determine if we're dealing with hourly data by checking time span
          let isHourlyData = false;
          if (lines[0].data.length > 1) {
            try {
              const firstDate = new Date(lines[0].data[0].x as string);
              const lastDate = new Date(lines[0].data[dataLength - 1].x as string);
              const timeSpanMs = lastDate.getTime() - firstDate.getTime();
              const timeSpanHours = timeSpanMs / (1000 * 60 * 60);
              // If we have hourly-like data (many points over hours/days), likely hourly
              isHourlyData = timeSpanHours > 12 && dataLength >= 12;
            } catch {
              // Not a date, continue with normal logic
            }
          }

          if (dataLength <= 4) {
            // If we have 4 or fewer data points, show all of them
            for (let i = 0; i < dataLength; i++) {
              labelIndices.push(i);
            }
          } else if (isHourlyData) {
            // For hourly data (48+ hours), show only 3 labels to prevent overlap
            labelIndices.push(0); // First
            const middleIndex = Math.floor((dataLength - 1) / 2);
            labelIndices.push(middleIndex); // Middle
            labelIndices.push(dataLength - 1); // Last
          } else if (dataLength <= 8) {
            // For 5-8 data points, show max 3 labels: first, middle, last
            labelIndices.push(0); // First
            if (dataLength > 2) {
              const middleIndex = Math.floor((dataLength - 1) / 2);
              if (middleIndex !== 0 && middleIndex !== dataLength - 1) {
                labelIndices.push(middleIndex); // Middle
              }
            }
            if (dataLength > 1) {
              labelIndices.push(dataLength - 1); // Last
            }
          } else {
            // Calculate evenly spaced indices including first and last
            for (let i = 0; i < maxLabels; i++) {
              const index = Math.round((dataLength - 1) * (i / (maxLabels - 1)));
              labelIndices.push(index);
            }
          }
          
          // Remove duplicates and sort
          const uniqueIndices = Array.from(new Set(labelIndices)).sort((a, b) => a - b);
          
          // Smart date formatting function
          const formatDateLabel = (dateStr: string | number) => {
            if (typeof dateStr === 'number') return dateStr.toString();
            
            try {
              const date = new Date(dateStr);
              if (isNaN(date.getTime())) return dateStr.toString();
              
              // Get first and last dates to determine time span
              const firstDate = new Date(lines[0].data[0].x as string);
              const lastDate = new Date(lines[0].data[dataLength - 1].x as string);
              const timeSpanMs = lastDate.getTime() - firstDate.getTime();
              const timeSpanDays = timeSpanMs / (1000 * 60 * 60 * 24);
              
              // Format based on time span
              if (timeSpanDays > 1095) { // More than 3 years - show years
                return date.getFullYear().toString();
              } else if (timeSpanDays > 90) { // More than 3 months - show "Mar 2023" format
                return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
              } else if (timeSpanDays > 2) { // More than 2 days - show "Mar 1" format
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              } else { // Less than 2 days - show "Mar 1 06:00" format with UTC
                const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
                const day = date.getUTCDate();
                const hours = String(date.getUTCHours()).padStart(2, '0');
                const minutes = String(date.getUTCMinutes()).padStart(2, '0');
                if (date.getUTCMinutes() > 0) {
                  return `${month} ${day} ${hours}:${minutes}`;
                } else {
                  return `${month} ${day} ${hours}:00`;
                }
              }
            } catch {
              return dateStr.toString();
            }
          };
          
          return uniqueIndices.map((index) => {
            const xPos = margin.left + (dataLength === 1 ? chartWidth / 2 : (index / (dataLength - 1)) * chartWidth);
            const label = formatDateLabel(lines[0].data[index].x);
            
            return (
              <text
                key={`x-label-${index}`}
                x={xPos}
                y={margin.top + chartHeight + 20}
                textAnchor={index === 0 ? "start" : index === dataLength - 1 ? "end" : "middle"}
                dominantBaseline="middle"
                fontSize="10"
                fill="#9CA3AF"
              >
                {label}
              </text>
            );
          });
        })()}
        
        {/* Data lines */}
        {lines.map((line, lineIndex) => {
          const useSecondaryAxis = line.axis === 'secondary';
          const yScale = useSecondaryAxis ? scaleSecondaryY : scaleY;
          
          return (
            <g key={lineIndex}>
              <path
                d={generatePath(line.data, useSecondaryAxis)}
                fill="none"
                stroke={line.color || DEFAULT_COLORS[lineIndex % DEFAULT_COLORS.length]}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                transform={`translate(${margin.left}, ${margin.top})`}
              />
              
              {/* Data points (only show on hover, skip null/gap values) */}
              {hoveredPoint?.values
                .filter((value) => value.lineIndex === lineIndex && value.y !== null)
                .map((value) => (
                  <circle
                    key={`${lineIndex}-${hoveredPoint.pointIndex}`}
                    cx={hoveredPoint.x}
                    cy={margin.top + value.y!}
                    r={5}
                    fill={line.color || DEFAULT_COLORS[lineIndex % DEFAULT_COLORS.length]}
                    stroke="white"
                    strokeWidth={2}
                  />
                ))}
            </g>
          );
        })}

        {/* Hover line */}
        {hoveredPoint && (
          <line
            x1={hoveredPoint.x}
            y1={margin.top}
            x2={hoveredPoint.x}
            y2={margin.top + chartHeight}
            stroke="#111827"
            strokeWidth={1}
          />
        )}
      </svg>
      
      {/* Tooltip */}
      {hoveredPoint && (
        <div
          className="absolute z-10 bg-gray-900 text-white text-xs rounded px-2 py-1 pointer-events-none"
          style={{
            left: hoveredPoint.x + 10,
            top: margin.top + 10,
            transform: 'translateY(-100%)'
          }}
        >
          {hoveredPoint.xValue !== undefined && (
            <div className="font-medium">
              {(() => {
                // Format tooltip date to exclude seconds
                if (typeof hoveredPoint.xValue === 'string') {
                  try {
                    const date = new Date(hoveredPoint.xValue);
                    if (!isNaN(date.getTime())) {
                      // Check if it's a time-based date (has hours/minutes)
                      if (date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0) {
                        // Show date and time in UTC without seconds
                        const year = date.getUTCFullYear();
                        const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
                        const day = date.getUTCDate();
                        const hours = String(date.getUTCHours()).padStart(2, '0');
                        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
                        return `${month} ${day}, ${year} ${hours}:${minutes} UTC`;
                      } else {
                        // Just show date
                        return date.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          timeZone: 'UTC'
                        });
                      }
                    }
                  } catch {
                    // Fall back to original value if parsing fails
                  }
                }
                return hoveredPoint.xValue;
              })()}
            </div>
          )}
          {hoveredPoint.values.map((value) => (
            <div key={`tooltip-${value.lineIndex}`}>
              {lines[value.lineIndex].name}: {value.value.y !== null ? value.value.y.toFixed(2) : 'N/A'}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MiniLineChart;